const RECENT_SEARCHES_KEY = 'pawsome-recent-searches';

const escapeHtml = (unsafe) => {
  const el = document.createElement('div');
  el.textContent = unsafe;
  return el.innerHTML;
};

function parseIdleConfig() {
  const el = document.getElementById('PredictiveSearchIdleConfig');

  if (!el) {
    return {
      enabled: false,
      popularTerms: [],
      recentLimit: 8,
      recentLabel: '',
      popularLabel: '',
    };
  }

  try {
    const raw = el.textContent.trim();
    const parsed = JSON.parse(raw);

    return {
      enabled: Boolean(parsed.enabled),
      popularTerms: Array.isArray(parsed.popularTerms) ? parsed.popularTerms : [],
      recentLimit: typeof parsed.recentLimit === 'number' ? parsed.recentLimit : 8,
      recentLabel: typeof parsed.recentLabel === 'string' ? parsed.recentLabel : '',
      popularLabel: typeof parsed.popularLabel === 'string' ? parsed.popularLabel : '',
    };
  } catch {
    return {
      enabled: false,
      popularTerms: [],
      recentLimit: 8,
      recentLabel: '',
      popularLabel: '',
    };
  }
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.#idleConfig = parseIdleConfig();

    if (!this.input || !this.predictiveSearchResults) {
      return;
    }

    this.setupEventListeners();
  }

  /** @type {{ enabled: boolean, popularTerms: string[], recentLimit: number, recentLabel: string, popularLabel: string }} */
  #idleConfig;

  setupEventListeners() {
    const form = this.querySelector('form.search');

    if (!form) {
      return;
    }

    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.predictiveSearchResults.addEventListener('click', (event) => {
      const link = event.target.closest('a.predictive-search__item');

      if (!link) {
        return;
      }

      const pill = link.querySelector('.button-secondary-slim');
      const term = pill ? pill.textContent.trim() : '';

      if (term) {
        this.#recordRecentSearch(term);
      }
    });

    this.input.addEventListener(
      'input',
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input?.value.trim() ?? '';
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.#showIdleSuggestionsOrClose();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    const q = this.getQuery();

    if (!q.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    this.#recordRecentSearch(q);
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.#showIdleSuggestionsOrClose();
      return;
    }

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.#showIdleSuggestionsOrClose();
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
      case 'ArrowDown':
      case 'Enter':
        event.preventDefault();
        break;
      default:
        return;
    }

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  #getRecentSearches() {
    try {
      const raw = window.localStorage.getItem(RECENT_SEARCHES_KEY);

      if (!raw) {
        return [];
      }

      const parsed = JSON.parse(raw);

      return Array.isArray(parsed) ? parsed.filter((t) => typeof t === 'string' && t.trim().length) : [];
    } catch {
      return [];
    }
  }

  #saveRecentSearches(terms) {
    try {
      window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(terms));
    } catch {
      /* quota or private mode */
    }
  }

  #recordRecentSearch(term) {
    const trimmed = term.trim();

    if (!trimmed.length) {
      return;
    }

    const limit = this.#idleConfig.recentLimit;
    let next = this.#getRecentSearches().filter((t) => t.toLowerCase() !== trimmed.toLowerCase());

    next.unshift(trimmed);
    next = next.slice(0, limit);
    this.#saveRecentSearches(next);
  }

  #showIdleSuggestionsOrClose() {
    if (!this.#idleConfig.enabled) {
      this.close(false);
      return;
    }

    const recent = this.#getRecentSearches();
    const popular = this.#idleConfig.popularTerms.map((t) => String(t).trim()).filter(Boolean);

    if (!recent.length && !popular.length) {
      this.close(false);
      return;
    }

    this.#renderIdleSuggestions(recent, popular);
  }

  #renderIdleSuggestions(recent, popular) {
    const searchRoot = window.routes && window.routes.search_url ? window.routes.search_url : '/search';

    let groupsHtml = '';

    if (recent.length && this.#idleConfig.recentLabel.trim()) {
      groupsHtml += this.#buildQueryGroupHtml(
        this.#idleConfig.recentLabel.trim(),
        'predictive-search-idle-recent-list',
        recent,
        searchRoot,
      );
    }

    if (popular.length && this.#idleConfig.popularLabel.trim()) {
      groupsHtml += this.#buildQueryGroupHtml(
        this.#idleConfig.popularLabel.trim(),
        'predictive-search-idle-popular-list',
        popular,
        searchRoot,
      );
    }

    if (!groupsHtml) {
      this.close(false);
      return;
    }

    const markup = `<div id="predictive-search-results" class="predictive-search-idle"><div class="predictive-search__results-groups-wrapper">${groupsHtml}</div></div>`;

    this.predictiveSearchResults.innerHTML = markup;
    this.setAttribute('results', true);
    this.removeAttribute('loading');
    this.open();
  }

  #buildQueryGroupHtml(heading, listId, terms, searchRoot) {
    const items = terms
      .map((term) => {
        const safeText = escapeHtml(term);
        const href = `${searchRoot}?q=${encodeURIComponent(term)}`;

        return `<li class="predictive-search__list-item" role="option" aria-selected="false"><a href="${href}" class="predictive-search__item" tabindex="-1"><span class="button-secondary-slim">${safeText}</span></a></li>`;
      })
      .join('');

    return `<div class="predictive-search__result-group predictive-search__result-group--queries predictive-search__result-group--idle"><h3 class="predictive-search__heading caption-with-letter-spacing">${escapeHtml(heading)}</h3><ul id="${listId}" class="predictive-search__results-queries-list list-unstyled" role="group">${items}</ul></div>`;
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) {
      return;
    }

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li[role="option"]');
    let activeElement = null;

    if (moveUp && !selectedElement) {
      return;
    }

    if (this.statusElement) {
      this.statusElement.textContent = '';
    }

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    } else {
      activeElement = allElements[0];
    }

    if (activeElement) {
      activeElement.setAttribute('aria-selected', true);

      if (selectedElement) {
        selectedElement.setAttribute('aria-selected', false);
      }

      activeElement.scrollIntoView({ block: 'nearest' });
    }
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  getSearchResults(searchTerm) {
    const predictiveUrl = window.routes?.predictive_search_url;

    if (!predictiveUrl) {
      this.close(false);
      return;
    }

    const queryKey = searchTerm.replaceAll(' ', '-').toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetchHTML(
      `${predictiveUrl}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=product,collection,query&resources[limit]=10&resources[limit_scope]=each`,
    )
      .then((html) => {
        const sectionRoot = html.querySelector('#shopify-section-predictive-search');

        if (!sectionRoot) {
          this.close(false);
          return;
        }

        const resultsMarkup = sectionRoot.innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch(() => {
        this.close(false);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingTextElement =
      this.loadingTextElement || this.querySelector('#predictive-search-option-search-keywords');

    this.setAttribute('loading', true);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) {
      return;
    }

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setAttribute('open', true);
  }

  open() {
    if (!this.input) {
      return;
    }

    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.removeAttribute('loading');

    if (!this.input) {
      return;
    }

    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute('aria-selected', false);
    }

    this.input.setAttribute('aria-expanded', false);
    this.removeAttribute('open');
    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
